import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBaseUrl, publicMediaSrc } from "../config/api";
import { adminJson } from "../utils/adminSession";
import AdminConfirmDialog from "../components/AdminConfirmDialog";

const EMPTY = {
  productName: "",
  slug: "",
  category: "",
  image: "",
  imageAlt: "",
  description: "",
  brand: "",
  sku: "",
  modelNumber: "",
  searchTags: [],
  seoTitle: "",
  seoDescription: "",
  isPublished: true,
};
const NAME_MAX = 150;
const SLUG_MAX = 180;
const ALT_MAX = 160;
const BRAND_MAX = 100;
const SKU_MAX = 100;
const MODEL_MAX = 100;
const SEO_TITLE_MAX = 60;
const SEO_DESC_MAX = 160;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const PAGE_SIZE = 5;
const PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const PREVIEW_HOST = "atindustrialsupply.com";
const inputClass =
  "w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-at-dark outline-none focus:border-at-gold";

function toSlug(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX);
}

function isPublished(item) {
  return item?.isPublished !== false;
}

function categoryIdOf(item) {
  if (!item?.category) return "";
  if (typeof item.category === "object") return String(item.category._id || "");
  return String(item.category);
}

function categoryNameOf(item) {
  if (item?.category && typeof item.category === "object") {
    return item.category.categoryName || "—";
  }
  return "—";
}

function categorySlugOf(item) {
  if (item?.category && typeof item.category === "object") {
    return item.category.slug || "";
  }
  return "";
}

function productUrlPrefix(categorySlug) {
  const slug = String(categorySlug || "").trim();
  return slug ? `/category/${slug}/` : "/category/";
}

function productPublicPath(item) {
  if (!item?.slug) return "";
  return `${productUrlPrefix(categorySlugOf(item))}${item.slug}`;
}

function formatUpdatedAt(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function AdminProductsPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const slugEdited = useRef(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [siteName, setSiteName] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((item) => {
      const hay = [
        item.productName,
        item.slug,
        item.sku,
        item.modelNumber,
        item.brand,
      ]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");
      const searchMatch = !q || hay.includes(q);
      const catMatch = categoryFilter === "all" || categoryIdOf(item) === categoryFilter;
      const published = isPublished(item);
      const statusMatch =
        statusFilter === "all" || (statusFilter === "published" && published) || (statusFilter === "draft" && !published);
      return searchMatch && catMatch && statusMatch;
    });
  }, [products, search, categoryFilter, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [productList, categoryList, contacts] = await Promise.all([
          fetch(`${getApiBaseUrl()}/products`, { headers: { Accept: "application/json" } }).then((res) => {
            if (!res.ok) throw new Error("Unable to load products");
            return res.json();
          }),
          fetch(`${getApiBaseUrl()}/categories`, { headers: { Accept: "application/json" } }).then((res) => {
            if (!res.ok) throw new Error("Unable to load categories");
            return res.json();
          }),
          fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } }).then((res) => (res.ok ? res.json() : [])),
        ]);
        if (!cancelled) {
          setProducts(Array.isArray(productList) ? productList : []);
          setCategories(Array.isArray(categoryList) ? categoryList : []);
          setSiteName(Array.isArray(contacts) && contacts[0]?.siteName ? contacts[0].siteName : "");
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Unable to load products");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter, statusFilter]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [panelOpen]);

  function onUnauthorized(err) {
    if (err?.status === 401) {
      navigate("/login", { replace: true });
      return true;
    }
    return false;
  }

  function openCreate() {
    slugEdited.current = false;
    setEditingId(null);
    setForm(EMPTY);
    setTagInput("");
    setError("");
    setNotice("");
    setPanelOpen(true);
  }

  function openEdit(item) {
    slugEdited.current = true;
    setEditingId(item._id);
    setForm({
      productName: item.productName || "",
      slug: item.slug || "",
      category: categoryIdOf(item),
      image: item.image || "",
      imageAlt: item.imageAlt || "",
      description: item.description || "",
      brand: item.brand || "",
      sku: item.sku || "",
      modelNumber: item.modelNumber || "",
      searchTags: Array.isArray(item.searchTags) ? item.searchTags : [],
      seoTitle: item.seoTitle || "",
      seoDescription: item.seoDescription || "",
      isPublished: isPublished(item),
    });
    setTagInput("");
    setError("");
    setNotice("");
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setEditingId(null);
    setForm(EMPTY);
    setTagInput("");
    slugEdited.current = false;
  }

  function onNameChange(value) {
    setForm((prev) => ({
      ...prev,
      productName: value,
      slug: slugEdited.current ? prev.slug : toSlug(value),
    }));
  }

  function addTag(raw) {
    const tag = String(raw || "").trim().toLowerCase();
    if (!tag) return;
    setForm((prev) => {
      if (prev.searchTags.includes(tag)) return prev;
      return { ...prev, searchTags: [...prev.searchTags, tag] };
    });
    setTagInput("");
  }

  function removeTag(tag) {
    setForm((prev) => ({ ...prev, searchTags: prev.searchTags.filter((item) => item !== tag) }));
  }

  async function onPickFile(file) {
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      setError("Product image must be a JPEG, PNG or WebP file");
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      setError("Product image must be 5 MB or smaller");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(new Error("Unable to read file"));
        reader.readAsDataURL(file);
      });
      const uploaded = await adminJson("/media", {
        method: "POST",
        body: {
          purpose: "products",
          filename: file.name,
          contentType: file.type,
          data,
        },
      });
      setForm((prev) => ({ ...prev, image: uploaded.path }));
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to upload image");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onSave(publish) {
    const name = form.productName.trim();
    const slug = form.slug.trim();
    const alt = form.imageAlt.trim();
    const description = form.description.trim();
    if (!name) {
      setError("Product name is required");
      return;
    }
    if (name.length > NAME_MAX) {
      setError(`Product name must be ${NAME_MAX} characters or fewer`);
      return;
    }
    if (!slug || !SLUG_RE.test(slug)) {
      setError("Slug can only contain lowercase letters, numbers and hyphens");
      return;
    }
    if (!form.category) {
      setError("Category is required");
      return;
    }
    if (!form.image.trim()) {
      setError("Product image is required");
      return;
    }
    if (!alt) {
      setError("Image alt text is required");
      return;
    }
    if (!description) {
      setError("Description is required");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        productName: name,
        slug,
        category: form.category,
        image: form.image.trim(),
        imageAlt: alt,
        description,
        brand: form.brand.trim(),
        sku: form.sku.trim().toUpperCase(),
        modelNumber: form.modelNumber.trim(),
        searchTags: form.searchTags,
        seoTitle: form.seoTitle.trim(),
        seoDescription: form.seoDescription.trim(),
        isPublished: Boolean(publish),
      };
      if (editingId) {
        const saved = await adminJson(`/products/${editingId}`, { method: "PUT", body: payload });
        setProducts((prev) => prev.map((item) => (item._id === saved._id ? saved : item)));
        setNotice("Product updated.");
      } else {
        const created = await adminJson("/products", { method: "POST", body: payload });
        setProducts((prev) => [created, ...prev]);
        setNotice("Product created.");
      }
      closePanel();
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to save product");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(item) {
    setDeleteTarget(item);
  }

  async function confirmDelete() {
    const item = deleteTarget;
    if (!item) return;
    setError("");
    setNotice("");
    setSaving(true);
    try {
      await adminJson(`/products/${item._id}`, { method: "DELETE" });
      setProducts((prev) => prev.filter((row) => row._id !== item._id));
      setNotice("Product deleted.");
      setDeleteTarget(null);
      if (editingId === item._id) closePanel();
    } catch (err) {
      if (onUnauthorized(err)) return;
      setError(err?.message || "Unable to delete product");
    } finally {
      setSaving(false);
    }
  }

  const photoSrc = publicMediaSrc(form.image);
  const from = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const to = Math.min(filtered.length, currentPage * PAGE_SIZE);
  const selectedCategorySlug = categories.find((item) => item._id === form.category)?.slug || "";
  const slugPrefix = productUrlPrefix(selectedCategorySlug);
  const previewTitle = form.seoTitle.trim() || form.productName.trim() || "Product title";
  const previewUrl = `${PREVIEW_HOST}${slugPrefix}${form.slug.trim() || "slug"}`;
  const previewDesc = form.seoDescription.trim() || form.description.trim() || "Write a short description for Google search results.";

  return (
    <div className="-mt-2">
      <p className="text-xs text-neutral-400">Dashboard / Products</p>
      <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-at-dark">Products</h1>
          <p className="mt-1 text-sm text-neutral-500">Create and manage your product catalogue and search appearance.</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-md bg-at-gold px-4 py-2 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
        >
          + Add Product
        </button>
      </div>

      {error && !panelOpen && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {notice && <p className="mt-4 text-sm text-emerald-700">{notice}</p>}

      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <p className="text-sm font-medium text-at-dark">
          {loading ? "Loading…" : `${filtered.length} ${filtered.length === 1 ? "Product" : "Products"}`}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by product, SKU or model..."
            className={`${inputClass} max-w-xs`}
          />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={`${inputClass} w-auto`}
            aria-label="Filter by category"
          >
            <option value="all">All Categories</option>
            {categories.map((item) => (
              <option key={item._id} value={item._id}>
                {item.categoryName}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`${inputClass} w-auto`}
            aria-label="Filter by status"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-neutral-500">Loading products…</p>
      ) : filtered.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-neutral-300 bg-white px-6 py-16 text-center">
          <p className="text-base font-semibold text-at-dark">No products yet</p>
          <p className="mt-1 text-sm text-neutral-500">Add a product to your catalogue.</p>
          <button
            type="button"
            onClick={openCreate}
            className="mt-5 rounded-md bg-at-gold px-4 py-2 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
          >
            + Add Product
          </button>
        </div>
      ) : (
        <div className="mt-5 overflow-x-auto rounded-xl border border-neutral-200 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-neutral-100 text-xs font-medium uppercase tracking-wide text-neutral-400">
              <tr>
                <th className="px-4 py-3 font-medium">Product</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">SKU / Model</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Updated</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((item) => (
                <tr key={item._id} className="border-b border-neutral-100 last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 shrink-0 overflow-hidden rounded bg-neutral-100">
                        {publicMediaSrc(item.image) ? (
                          <img src={publicMediaSrc(item.image)} alt={item.imageAlt || ""} className="h-full w-full object-cover" />
                        ) : null}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-at-dark">{item.productName}</p>
                        {item.slug ? <p className="text-xs text-neutral-400">{productPublicPath(item)}</p> : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{categoryNameOf(item)}</td>
                  <td className="px-4 py-3 text-neutral-600">
                    <p>{item.sku || "—"}</p>
                    <p className="text-xs text-neutral-400">{item.modelNumber ? `Model: ${item.modelNumber}` : "—"}</p>
                  </td>
                  <td className="px-4 py-3">
                    {isPublished(item) ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        Published
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500">
                        <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" />
                        Draft
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-neutral-500">{formatUpdatedAt(item.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        className="rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-at-dark"
                        aria-label={`Edit ${item.productName}`}
                      >
                        <PencilIcon />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(item)}
                        className="rounded-md p-2 text-neutral-500 hover:bg-red-50 hover:text-red-600"
                        aria-label={`Delete ${item.productName}`}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="mt-6 flex flex-col items-center justify-between gap-3 text-sm text-neutral-500 sm:flex-row">
          <p>
            Showing {from}–{to} of {filtered.length} products
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-md border border-neutral-200 bg-white px-3 py-1.5 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="rounded-md bg-at-gold px-3 py-1.5 text-at-dark">{currentPage}</span>
            <button
              type="button"
              disabled={currentPage >= pageCount}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              className="rounded-md border border-neutral-200 bg-white px-3 py-1.5 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {panelOpen && (
        <div className="fixed inset-0 z-50 flex h-[100dvh] max-h-[100dvh] justify-end overflow-hidden bg-black/30">
          <button type="button" aria-label="Close panel" className="hidden h-full flex-1 sm:block" onClick={closePanel} />
          <aside className="flex h-full min-h-0 w-full max-w-lg flex-col overflow-hidden bg-white shadow-xl">
            <div className="flex shrink-0 items-start justify-between border-b border-neutral-100 px-6 py-5">
              <div>
                <h2 className="text-xl font-semibold text-at-dark">{editingId ? "Edit Product" : "Add Product"}</h2>
                <p className="mt-1 text-sm text-neutral-500">
                  {editingId ? "Update this product in your catalogue." : "Create a new product for your catalogue."}
                </p>
              </div>
              <button type="button" onClick={closePanel} className="text-2xl leading-none text-neutral-400 hover:text-at-dark" aria-label="Close">
                ×
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5">
              {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

              <h3 className="text-sm font-semibold text-at-dark">Product Information</h3>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-at-dark" htmlFor="productName">
                    Product Name *
                  </label>
                  <input
                    id="productName"
                    value={form.productName}
                    maxLength={NAME_MAX}
                    onChange={(e) => onNameChange(e.target.value)}
                    placeholder="e.g. Mild Steel Round Pipe"
                    className={`${inputClass} mt-1.5`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-at-dark" htmlFor="slug">
                    URL Slug *
                  </label>
                  <div className="mt-1.5 flex overflow-hidden rounded-md border border-neutral-200 focus-within:border-at-gold">
                    <span className="shrink-0 bg-neutral-100 px-2 py-2 text-xs text-neutral-500">{slugPrefix}</span>
                    <input
                      id="slug"
                      value={form.slug}
                      maxLength={SLUG_MAX}
                      onChange={(e) => {
                        slugEdited.current = true;
                        setForm((prev) => ({ ...prev, slug: e.target.value.toLowerCase() }));
                      }}
                      placeholder="mild-steel-round-pipe"
                      className="w-full bg-white px-2 py-2 text-sm text-at-dark outline-none"
                    />
                  </div>
                </div>
              </div>

              <label className="mt-4 block text-sm font-medium text-at-dark" htmlFor="category">
                Category *
              </label>
              <select
                id="category"
                value={form.category}
                onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                className={`${inputClass} mt-1.5`}
              >
                <option value="">Select a category</option>
                {categories.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.categoryName}
                  </option>
                ))}
              </select>

              <p className="mt-4 text-sm font-medium text-at-dark">Product Image *</p>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  onPickFile(e.dataTransfer.files?.[0]);
                }}
                className={`mt-1.5 rounded-lg border-2 border-dashed px-4 py-8 text-center ${
                  dragOver ? "border-at-gold bg-amber-50" : "border-neutral-200 bg-neutral-50"
                }`}
              >
                {photoSrc ? (
                  <img src={photoSrc} alt={form.imageAlt || ""} className="mx-auto mb-3 h-20 w-full object-contain" />
                ) : (
                  <p className="mb-2 text-2xl text-neutral-300">▢</p>
                )}
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  className="text-sm font-medium text-at-dark underline disabled:opacity-60"
                >
                  {uploading ? "Uploading…" : "Upload product image"}
                </button>
                <p className="mt-2 text-xs text-neutral-400">JPG, PNG or WebP · Recommended 1200 × 1200 px · Max 5 MB</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => onPickFile(e.target.files?.[0])}
                />
              </div>

              <label className="mt-4 block text-sm font-medium text-at-dark" htmlFor="imageAlt">
                Image Alt Text *
              </label>
              <input
                id="imageAlt"
                value={form.imageAlt}
                maxLength={ALT_MAX}
                onChange={(e) => setForm((prev) => ({ ...prev, imageAlt: e.target.value }))}
                placeholder="Describe the product image"
                className={`${inputClass} mt-1.5`}
              />
              <p className="mt-1 text-right text-xs text-neutral-400">
                {form.imageAlt.length} / {ALT_MAX}
              </p>

              <label className="mt-4 block text-sm font-medium text-at-dark" htmlFor="description">
                Description *
              </label>
              <textarea
                id="description"
                rows={6}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the product, uses and applications"
                className={`${inputClass} mt-1.5 resize-y`}
              />
              <p className="mt-1 text-right text-xs text-neutral-400">{form.description.length}</p>

              <h3 className="mt-8 text-sm font-semibold text-at-dark">Product Details</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-at-dark" htmlFor="brand">
                    Brand
                  </label>
                  <input
                    id="brand"
                    value={form.brand}
                    maxLength={BRAND_MAX}
                    onChange={(e) => setForm((prev) => ({ ...prev, brand: e.target.value }))}
                    placeholder="Optional"
                    className={`${inputClass} mt-1.5`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-at-dark" htmlFor="sku">
                    SKU
                  </label>
                  <input
                    id="sku"
                    value={form.sku}
                    maxLength={SKU_MAX}
                    onChange={(e) => setForm((prev) => ({ ...prev, sku: e.target.value.toUpperCase() }))}
                    placeholder="e.g. AT-MS-001"
                    className={`${inputClass} mt-1.5`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-at-dark" htmlFor="modelNumber">
                    Model Number
                  </label>
                  <input
                    id="modelNumber"
                    value={form.modelNumber}
                    maxLength={MODEL_MAX}
                    onChange={(e) => setForm((prev) => ({ ...prev, modelNumber: e.target.value }))}
                    placeholder="Optional"
                    className={`${inputClass} mt-1.5`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-at-dark" htmlFor="searchTags">
                    Search Tags
                  </label>
                  <div className="mt-1.5 flex gap-2">
                    <input
                      id="searchTags"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTag(tagInput);
                        }
                      }}
                      placeholder="Add a tag..."
                      className={inputClass}
                    />
                    <button
                      type="button"
                      onClick={() => addTag(tagInput)}
                      className="shrink-0 rounded-md border border-neutral-200 px-3 py-2 text-sm text-at-dark"
                    >
                      Add
                    </button>
                  </div>
                  {form.searchTags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {form.searchTags.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs text-at-dark"
                        >
                          {tag} ×
                        </button>
                      ))}
                    </div>
                  )}
                 
                </div>
              </div>

              <h3 className="mt-8 text-sm font-semibold text-at-dark">SEO Settings</h3>
              <label className="mt-4 block text-sm font-medium text-at-dark" htmlFor="seoTitle">
                SEO Title
              </label>
              <input
                id="seoTitle"
                value={form.seoTitle}
                maxLength={SEO_TITLE_MAX}
                onChange={(e) => setForm((prev) => ({ ...prev, seoTitle: e.target.value }))}
                placeholder={siteName ? `Mild Steel Round Pipe Supplier Malaysia | ${siteName}` : "Mild Steel Round Pipe Supplier Malaysia"}
                className={`${inputClass} mt-1.5`}
              />
              <p className="mt-1 text-right text-xs text-neutral-400">
                {form.seoTitle.length} / {SEO_TITLE_MAX}
              </p>

              <label className="mt-4 block text-sm font-medium text-at-dark" htmlFor="seoDescription">
                SEO Description
              </label>
              <textarea
                id="seoDescription"
                rows={3}
                value={form.seoDescription}
                maxLength={SEO_DESC_MAX}
                onChange={(e) => setForm((prev) => ({ ...prev, seoDescription: e.target.value }))}
                placeholder="Write a short description for Google search results"
                className={`${inputClass} mt-1.5 resize-y`}
              />
              <p className="mt-1 text-right text-xs text-neutral-400">
                {form.seoDescription.length} / {SEO_DESC_MAX}
              </p>

              <p className="mt-4 text-sm font-medium text-at-dark">Google Search Preview</p>
              <div className="mt-1.5 rounded-md border border-neutral-200 bg-white px-3 py-3">
                <p className="text-sm text-[#1a0dab]">{previewTitle}</p>
                <p className="truncate text-xs text-emerald-700">{previewUrl}</p>
                <p className="mt-1 text-xs leading-snug text-neutral-500">{previewDesc}</p>
              </div>
            </div>

            <div className="shrink-0 border-t border-neutral-100 px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-at-dark">Publishing</span>
                <label className="flex items-center gap-2 text-sm text-neutral-600">
                  <span className={`relative h-5 w-9 rounded-full ${form.isPublished ? "bg-at-gold" : "bg-neutral-300"}`}>
                    <input
                      type="checkbox"
                      className="peer absolute inset-0 cursor-pointer opacity-0"
                      checked={Boolean(form.isPublished)}
                      onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))}
                    />
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition ${
                        form.isPublished ? "left-4" : "left-0.5"
                      }`}
                    />
                  </span>
                  Publish product
                </label>
              </div>
              <p className="mb-3 text-xs text-neutral-400">Published products are visible on the website and included in the sitemap.</p>
              <div className="grid grid-cols-2 gap-2 sm:flex">
                <button
                  type="button"
                  onClick={closePanel}
                  className="rounded-md border border-neutral-300 px-3 py-2.5 text-sm font-medium text-at-dark"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={saving || uploading}
                  onClick={() => onSave(false)}
                  className="rounded-md border border-neutral-300 px-3 py-2.5 text-sm font-medium text-at-dark disabled:opacity-60 sm:flex-1"
                >
                  Save as Draft
                </button>
                <button
                  type="button"
                  disabled={saving || uploading}
                  onClick={() => onSave(true)}
                  className="col-span-2 rounded-md bg-at-gold px-3 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover disabled:opacity-60 sm:col-auto sm:flex-1"
                >
                  {saving ? "Saving…" : editingId ? "Save Changes" : "Create Product"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
      {deleteTarget ? (
        <AdminConfirmDialog
          title="Delete product?"
          message={`Delete “${deleteTarget.productName}”? You can restore it from Recent Activities for 30 days.`}
          busy={saving}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      ) : null}
    </div>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 7h16" />
      <path d="M9 7V5h6v2" />
      <path d="M7 7l1 13h8l1-13" />
    </svg>
  );
}

export default AdminProductsPage;
